import { useState, useEffect, useCallback, useRef } from 'react';
import { activityService } from '../../../services/activityService';
import { contactService } from '../../../services/contactService';
import { shippingSenderService } from '../../../services/shippingSenderService';
import { shipmentService } from '../../../services/shipmentService';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { GroupAwareActivityResponse } from '../../../types/activity';
import type { Contact } from '../../../types/contact';
import type { ShippingSender } from '../../../types/shippingSender';
import type { Shipment, PayerType, PaymentMethod } from '../../../types/shipment';
import type { CitySearchResult, WarehouseSearchResult, ContactPersonResult } from '../../../services/novaPoshtaClient';

export interface ShipmentFormValues {
  recipientLastName: string;
  recipientFirstName: string;
  recipientMiddleName: string;
  recipientPhone: string;
  recipientCity: CitySearchResult | null;
  recipientWarehouse: WarehouseSearchResult | null;
  senderId: number | '';
  senderContactPerson: ContactPersonResult | null;
  weight: string;
  seatsAmount: string;
  declaredValue: string;
  codEnabled: boolean;
  codAmount: string;
  payerType: PayerType;
  paymentMethod: PaymentMethod;
}

interface UseCreateShipmentParams {
  companyId: number;
  groupId: number;
}

function computeStockOutClientId(group: GroupAwareActivityResponse): number | null {
  const stockOutClientIds = new Set(
    group.activities
      .filter((a) => a.operationType === 'STOCK_OUT' && a.clientId)
      .map((a) => a.clientId as number)
  );
  return stockOutClientIds.size === 1 ? [...stockOutClientIds][0] : null;
}

export function useCreateShipment({ companyId, groupId }: UseCreateShipmentParams) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [group, setGroup] = useState<GroupAwareActivityResponse | null>(null);
  const [client, setClient] = useState<Contact | null>(null);
  const [senders, setSenders] = useState<ShippingSender[]>([]);
  const [existingShipment, setExistingShipment] = useState<Shipment | null>(null);
  // Resolved for whichever sender profile is currently selected (defaults to the first one
  // once senders load) — drives the recipient city/warehouse Autocompletes and the sender
  // contact person list. Same "transient, never persisted/logged" trust model as Фаза 1 —
  // see doc/shipping/novaposhta-integration.md#security.
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [selectSenderError, setSelectSenderError] = useState<string | null>(null);
  // Guards against an older selectSender(A) call resolving after a newer selectSender(B) —
  // without this, quickly switching senders could leave A's key active for a form that
  // visibly shows B selected, so recipient city/warehouse search would run under A's account.
  const selectSenderRequestId = useRef(0);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [refreshStatusError, setRefreshStatusError] = useState<string | null>(null);

  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [groupResponse, sendersResponse, existing] = await Promise.all([
        activityService.getActivityGroupById(groupId),
        shippingSenderService.listSenders(companyId),
        shipmentService.getByActivityGroupId(groupId),
      ]);
      setGroup(groupResponse);
      setSenders(sendersResponse);

      if (existing) {
        setExistingShipment(existing);
        setLoading(false);
        return;
      }

      // apiKey itself is resolved by the caller once a sender is selected (see
      // selectSender below) — CreateShipmentPage defaults to the first sender on load.
      const clientId = computeStockOutClientId(groupResponse);
      if (!clientId) {
        setEligibilityError('Продажні активності групи належать різним клієнтам або клієнт не вказаний');
        setLoading(false);
        return;
      }
      setClient(await contactService.getById(clientId));
    } catch {
      setError('Не вдалося завантажити дані для створення ТТН');
    } finally {
      setLoading(false);
    }
  }, [groupId, companyId]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-resolves the API key when the user picks a different sender profile in the form —
  // each profile is its own Nova Poshta account with its own contact persons/key.
  const selectSender = async (senderId: number) => {
    const requestId = ++selectSenderRequestId.current;
    setApiKey(null);
    setSelectSenderError(null);
    try {
      const key = await shippingSenderService.getApiKeyForFrontend(companyId, senderId);
      if (selectSenderRequestId.current !== requestId) return;
      setApiKey(key);
    } catch (err: unknown) {
      if (selectSenderRequestId.current !== requestId) return;
      setApiKey(null);
      setSelectSenderError(extractErrorMessage(err, 'Не вдалося отримати API-ключ цього відправника'));
    }
  };

  const submit = async (values: ShipmentFormValues): Promise<string | null> => {
    const fail = (message: string): string => {
      setSubmitError(message);
      return message;
    };

    if (!values.senderId) return fail('Оберіть відправника');
    const selectedSender = senders.find((s) => s.id === values.senderId);
    if (selectedSender && (!selectedSender.defaultSenderCityRef || !selectedSender.defaultSenderWarehouseRef)) {
      return fail('У цього профілю відправника не вказано місто й відділення — додайте адресу в налаштуваннях компанії');
    }
    if (!apiKey) return fail('API-ключ Нової пошти недоступний — оберіть відправника ще раз');
    if (!values.recipientCity || !values.recipientWarehouse) return fail('Оберіть місто й відділення отримувача');
    if (!values.senderContactPerson) return fail('Оберіть контактну особу відправника');
    if (!values.recipientLastName.trim() || !values.recipientFirstName.trim()) {
      return fail("Вкажіть прізвище та ім'я отримувача повністю");
    }
    if (!values.recipientPhone.trim()) return fail('Вкажіть телефон отримувача');
    const codAmount = values.codEnabled ? parseFloat(values.codAmount) : undefined;
    if (values.codEnabled && !(codAmount !== undefined && codAmount > 0)) {
      return fail('Вкажіть суму наложеного платежу більше нуля');
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      // Waybill creation itself (InternetDocument/save) now happens server-side — see
      // CreateNovaPoshtaDocumentUseCase.java — so a rejected/failed Nova Poshta call is
      // visible in our own backend logs instead of only the browser console.
      const shipment = await shipmentService.register({
        activityGroupId: groupId,
        senderId: values.senderId as number,
        recipientLastName: values.recipientLastName,
        recipientFirstName: values.recipientFirstName,
        recipientMiddleName: values.recipientMiddleName || undefined,
        recipientPhone: values.recipientPhone,
        recipientCityRef: values.recipientCity.ref,
        recipientWarehouseRef: values.recipientWarehouse.ref,
        recipientWarehouseNumber: values.recipientWarehouse.number,
        senderContactPersonRef: values.senderContactPerson.ref,
        senderPhone: values.senderContactPerson.phone,
        weight: parseFloat(values.weight),
        seatsAmount: parseInt(values.seatsAmount, 10),
        declaredValue: parseFloat(values.declaredValue),
        codAmount,
        payerType: values.payerType,
        paymentMethod: values.paymentMethod,
      });

      setExistingShipment(shipment);
      return null;
    } catch (err: unknown) {
      const message = extractErrorMessage(err, 'Не вдалося створити ТТН');
      setSubmitError(message);
      return message;
    } finally {
      setSubmitting(false);
    }
  };

  // Only status/updatedDate change on refresh — merge into the already-decrypted
  // existingShipment instead of re-decrypting recipientName/recipientPhone.
  const refreshStatus = async () => {
    if (!existingShipment) return;

    setRefreshingStatus(true);
    setRefreshStatusError(null);
    try {
      const refreshed = await shipmentService.refreshStatus(existingShipment.id);
      setExistingShipment((prev) => prev && {
        ...prev,
        status: refreshed.status,
        updatedDate: refreshed.updatedDate,
      });
    } catch {
      setRefreshStatusError('Не вдалося оновити статус');
    } finally {
      setRefreshingStatus(false);
    }
  };

  // Cancellation sets DEACTIVATED_DATE on SHIPMENT (Фаза 6) — the group is immediately free
  // for a new shipment, so drop existingShipment to null rather than just updating its status.
  const performCancel = async (action: (id: number) => Promise<Shipment>) => {
    if (!existingShipment) return;

    setCancelling(true);
    setCancelError(null);
    try {
      await action(existingShipment.id);
      setExistingShipment(null);
    } catch (err: unknown) {
      setCancelError(extractErrorMessage(err, 'Не вдалося скасувати ТТН'));
    } finally {
      setCancelling(false);
    }
  };

  const cancel = () => performCancel(shipmentService.cancel);

  // Used when Nova Poshta keeps rejecting InternetDocument/delete because the ЕН was already
  // voided directly in its own dashboard — marks the shipment cancelled in our DB only,
  // trusting the user's own Nova Poshta dashboard (see ShipmentService#forceCancelShipment).
  const forceCancel = () => performCancel(shipmentService.forceCancel);

  return {
    loading,
    error,
    eligibilityError,
    group,
    client,
    senders,
    existingShipment,
    apiKey,
    selectSender,
    selectSenderError,
    submit: {
      submitting,
      error: submitError,
      run: submit,
    },
    refreshStatus: {
      submitting: refreshingStatus,
      error: refreshStatusError,
      run: refreshStatus,
    },
    cancel: {
      submitting: cancelling,
      error: cancelError,
      run: cancel,
      forceRun: forceCancel,
    },
  };
}
